import React, { useMemo, useState } from 'react';
import { IconButton, Menu, MenuItem, Slider, Box } from '@mui/material';
import { DotsVertical } from '@untitledui-pro/icons/line';
import { MixerActions } from './MixerTypes';

// Store Zustand
import { getGrid } from '../../../../store/LoadingStore';
import type { MixerElement } from '@visualizer/types';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { getSentence } from '@gui/constants/sentences';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { isVectorialScalarModeToggleEnabled } from 'Simulations/SimConfig';
import { ScalarModeIcon, VectorialModeIcon } from './vectorialScalarModeIcons';

type MixerRowProps = {
    readonly mixerElement: MixerElement;
    actions: (action: MixerActions, layerId: string, value?: any) => void;
    multimode: boolean;
    hideSlider?: boolean;
};

const MixerRow: React.FC<MixerRowProps> = (props: MixerRowProps) => {
    const { mixerElement } = props;

    const layer = getGrid().searchLayerByUID(mixerElement.layerId);
    const mode = usePersistentStore((s) => s.layers.layerStates[mixerElement.layerId]?.VECTORIAL_SCALAR_MODE ?? 0);
    const updateLayerState = usePersistentStore((s) => s.updateLayerState);
    const simKind = layer?.getSimKind?.();
    const isModeToggleEnabled = simKind != null ? isVectorialScalarModeToggleEnabled(simKind) : true;
    const modeTooltipLabel = mode === 0 ? 'Vectorial mode' : 'Scalar mode';

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const openElMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const closeElMenu = () => setAnchorEl(null);

    const handleMenuAction = (action: MixerActions, value?: any) => {
        props.actions(action, mixerElement.layerId, value);
        closeElMenu();
    };

    const getTooltipTitle = (key: string) => {
        switch (key) {
            case 'invert':
                return 'Mixer.InvertGradient.Tooltip';
            case 'normalize':
                return 'Mixer.Normalize.Tooltip';
            default:
                return '';
        }
    };

    const isItemActive = (key: string) => {
        switch (key) {
            case 'showSensors':
                return !mixerElement.frameDrawOptions?.showSensors;
            case 'transparent':
                return !!mixerElement.frameDrawOptions?.transparent;
            case 'lock':
                return mixerElement.locked;
            case 'invert':
                return mixerElement.invert;
            case 'normalize':
                return mixerElement.isRangeNormalization;
            default:
                return false;
        }
    };

    const activeMenuItemSx = {
        backgroundColor: 'rgba(82, 170, 255, 0.22)',
        '&:hover': { backgroundColor: 'rgba(82, 170, 255, 0.22)' },
    };

    const menuConfig = useMemo(() => {
        const baseItems = [
            {
                key: 'delete',
                label: 'Delete',
                action: MixerActions.DELETE,
            },
        ];

        if (props.multimode) {
            baseItems.push({
                key: 'showSensors',
                label: mixerElement.frameDrawOptions?.showSensors ? 'Hide' : 'Show',
                action: MixerActions.TOGGLE_VISIBLE,
            });
            baseItems.push({
                key: 'transparent',
                label: mixerElement.frameDrawOptions?.transparent ? 'Opaque' : 'Transparent',
                action: MixerActions.TOGGLE_TRANSPARENCY,
            });
        }

        if (!props.multimode) {
            baseItems.push(
                {
                    key: 'lock',
                    label: mixerElement.locked ? 'Unlock' : 'Lock',
                    action: MixerActions.TOGGLE_LOCK,
                },
                {
                    key: 'invert',
                    label: 'Invert Gradient',
                    action: MixerActions.TOGGLE_INVERT,
                },
                {
                    key: 'normalize',
                    label: mixerElement.isRangeNormalization ? 'Range min/max to 1' : 'Range 0 to 1',
                    action: MixerActions.IS_0_1_NORMALIZATION,
                }
            );
        }

        return baseItems;
    }, [
        props.multimode,
        mixerElement.locked,
        mixerElement.isRangeNormalization,
        mixerElement.frameDrawOptions?.showSensors,
        mixerElement.frameDrawOptions?.transparent,
    ]);

    const layerSuffixes = !props.multimode
        ? ([mixerElement.invert ? 'inv' : null, mixerElement.isRangeNormalization ? '0-1' : null].filter(Boolean) as string[])
        : [];
    const layerLabel = `${layer?.name ?? ''}${layerSuffixes.length ? ` (${layerSuffixes.join(', ')})` : ''}`;

    return (
        <div key={mixerElement.layerId}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {/* Show colored circle in multimode */}
                    {props.multimode && (
                        <>
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: `#${mixerElement.frameDrawOptions?.sensorsMultiColor.toString(16).padStart(6, '0')}`,
                                    border: '1px solid #555',
                                }}
                            />
                            <CustomTooltip side="left" title={modeTooltipLabel}>
                                <button
                                    type="button"
                                    className={`layer-mode-toggle__button${!isModeToggleEnabled ? ' is-disabled' : ''}`}
                                    disabled={!isModeToggleEnabled}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (!isModeToggleEnabled) return;
                                        updateLayerState(mixerElement.layerId, { VECTORIAL_SCALAR_MODE: mode === 0 ? 1 : 0 });
                                    }}
                                    aria-label={modeTooltipLabel}
                                    data-testid={`mixer-row-mode-toggle-${mixerElement.layerId}`}
                                >
                                    {mode === 0 ? (
                                        <VectorialModeIcon className="layer-mode-toggle__icon" />
                                    ) : (
                                        <ScalarModeIcon className="layer-mode-toggle__icon" />
                                    )}
                                </button>
                            </CustomTooltip>
                        </>
                    )}
                    <span className="mixer-row-label">{layerLabel}</span>
                </div>
                <IconButton onClick={openElMenu}>
                    <DotsVertical className="icon-size-medium" />
                </IconButton>
            </div>

            {/* Only show slider if not in multimode */}
            {!props.multimode && !props.hideSlider && (
                <Box sx={{ pr: 1.7 }}>
                    <Slider
                        className="panel-slider"
                        valueLabelDisplay="on"
                        valueLabelFormat={(value) => Number(value.toFixed(2))}
                        size="small"
                        value={mixerElement.weight}
                        step={0.01}
                        min={0}
                        max={1}
                        onChange={(_, value) => props.actions(MixerActions.CHANGE_WEIGHT, mixerElement.layerId, value)}
                        sx={{
                            transition: 'none',
                            '& .MuiSlider-thumb': { transition: 'none' },
                            '& .MuiSlider-track': { transition: 'none' },
                            '& .MuiSlider-rail': { transition: 'none' },
                            '& .MuiSlider-mark': { transition: 'none' },
                        }}
                        disabled={mixerElement.locked}
                    />
                </Box>
            )}

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {menuConfig.map((item) => (
                    <MenuItem
                        key={item.key}
                        onClick={() => handleMenuAction(item.action)}
                        sx={isItemActive(item.key) ? activeMenuItemSx : undefined}
                    >
                        <CustomTooltip title={getSentence(getTooltipTitle(item.key))} side="left">
                            {item.label}
                        </CustomTooltip>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default MixerRow;
